import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import { usePageMeta } from "../../hooks/useMeta";
import { LandingHeader, LandingFooter } from "../../components/landing";
import Button from "../../components/buttons/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faCheck,
  faMobileScreen,
  faBell,
  faUtensils,
  faTimes,
  faHourglass,
  faWalking,
  faBagShopping,
} from "@fortawesome/pro-solid-svg-icons";
import { faArrowDown, faChevronDown } from "@fortawesome/pro-light-svg-icons";
import {
  faApple as faAppleBrand,
  faGooglePlay as faGooglePlayBrand,
} from "@fortawesome/free-brands-svg-icons";
import { playStoreUrl, appStoreUrl } from "../../utils/constants";
import useUserAgent from "../../hooks/useUserAgent";

// Images
import customerAppImg from "../../assets/img/landing/reworked/customer_app.webp";
import step1Img from "../../assets/img/landing/reworked/consumers_step1.webp";
import step2Img from "../../assets/img/landing/reworked/consumers_step2.webp";
import step3Img from "../../assets/img/landing/reworked/consumers_step3.webp";
import benefitsBgImg from "../../assets/img/landing/reworked/resto.webp";

function LandingConsumers() {
  const { t } = useTranslation();
  const userAgent = useUserAgent();

  // Parallax scroll effect
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Floating CTA visibility
  const [showFloatingCta, setShowFloatingCta] = useState(false);
  const heroRef = useRef(null);
  const finalCtaRef = useRef(null);

  // FAQ accordion state - first item open by default
  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  useEffect(() => {
    const heroHeight = heroRef.current?.offsetHeight || 600;
    const finalCtaTop = finalCtaRef.current?.offsetTop || Infinity;
    const windowHeight = window.innerHeight;

    const showThreshold = heroHeight * 0.8;
    const hideThreshold = finalCtaTop - windowHeight + 200;

    setShowFloatingCta(scrollY > showThreshold && scrollY < hideThreshold);
  }, [scrollY]);

  // Pre-order flow steps with images
  const steps = useMemo(
    () => [
      {
        icon: faMobileScreen,
        image: step1Img,
        title: t("landing.consumers.preorderFlow.step1.title"),
        description: t("landing.consumers.preorderFlow.step1.description"),
      },
      {
        icon: faBell,
        image: step2Img,
        title: t("landing.consumers.preorderFlow.step2.title"),
        description: t("landing.consumers.preorderFlow.step2.description"),
      },
      {
        icon: faUtensils,
        image: step3Img,
        title: t("landing.consumers.preorderFlow.step3.title"),
        description: t("landing.consumers.preorderFlow.step3.description"),
      },
    ],
    [t],
  );

  // FAQ items
  const faqs = useMemo(
    () => [
      {
        question: t("landing.consumers.faq.items.free.question"),
        answer: t("landing.consumers.faq.items.free.answer"),
      },
      {
        question: t("landing.consumers.faq.items.payment.question"),
        answer: t("landing.consumers.faq.items.payment.answer"),
      },
      {
        question: t("landing.consumers.faq.items.restaurants.question"),
        answer: t("landing.consumers.faq.items.restaurants.answer"),
      },
      {
        question: t("landing.consumers.faq.items.issue.question"),
        answer: t("landing.consumers.faq.items.issue.answer"),
      },
    ],
    [t],
  );

  // Image error handler
  const handleImageError = useCallback((e) => {
    e.target.style.opacity = "0.3";
    e.target.alt = "Image unavailable";
  }, []);

  // Open store links (smart redirect: Apple → App Store, Android → Play Store, web → Play Store default)
  const openPlayStore = () => window.open(playStoreUrl, "_blank");
  const openAppStore = () => window.open(appStoreUrl, "_blank");
  const openStoreForDevice = () => {
    if (userAgent === "Apple") openAppStore();
    else openPlayStore(); // Android, Computer, Unknown → default to Play Store
  };

  return (
    <>
      {usePageMeta("landingConsumers")}
      <div className="landing-consumer">
        {/* Parallax gradient accents */}
        <div
          className="landing-consumer__gradient-1"
          style={{ transform: `translateY(${scrollY * 0.15}px)` }}
        />
        <div
          className="landing-consumer__gradient-2"
          style={{ transform: `translateY(${scrollY * 0.1}px)` }}
        />

        {/* Skip to main content link for accessibility */}
        <a href="#main-content" className="landing-consumer__skip-link">
          {t("common.skipToContent", "Skip to main content")}
        </a>

        <LandingHeader />

        {/* Hero Section - Dark theme */}
        <section
          ref={heroRef}
          id="main-content"
          className="landing-hero-section landing-consumer__hero"
        >
          <div className="landing-hero-dots">
            {Array.from({ length: 40 }).map((_, i) => (
              <div
                key={i}
                className={`landing-hero-dot landing-hero-dot--${i % 8}`}
                style={{
                  left: `${5 + ((i * 11) % 90)}%`,
                  top: `${8 + ((i * 13) % 85)}%`,
                  animationDelay: `${i * 0.15}s`,
                }}
                aria-hidden="true"
              />
            ))}
          </div>
          <div className="landing-consumer__hero-content">
            <div className="landing-consumer__hero-text">
              {/* Tagline badge */}
              <span className="landing-consumer__tagline">
                {t("landing.consumers.hero.badge")}
              </span>

              <h1 className="landing-consumer__headline">
                {t("landing.consumers.hero.headline")}
              </h1>

              <p className="landing-consumer__subheadline">
                {t("landing.consumers.hero.subheadline")}
              </p>

              {/* App Store Buttons */}
              <div className="landing-consumer__store-buttons">
                <button
                  className="landing-consumer__store-button"
                  onClick={openPlayStore}
                  data-analytics="cta-consumer-playstore"
                  data-analytics-location="hero"
                >
                  <FontAwesomeIcon icon={faGooglePlayBrand} />
                  <div className="landing-consumer__store-button-text">
                    <span>{t("home.google.download")}</span>
                    <span>{t("home.google.store")}</span>
                  </div>
                </button>
                <button
                  className="landing-consumer__store-button"
                  onClick={openAppStore}
                  data-analytics="cta-consumer-appstore"
                  data-analytics-location="hero"
                >
                  <FontAwesomeIcon icon={faAppleBrand} />
                  <div className="landing-consumer__store-button-text">
                    <span>{t("home.apple.download")}</span>
                    <span>{t("home.apple.store")}</span>
                  </div>
                </button>
              </div>

              {/* Trust items */}
              <div className="landing-consumer__trust-items">
                <div className="landing-consumer__trust-item">
                  <FontAwesomeIcon icon={faCheck} />
                  <span>{t("landing.consumers.hero.trust1")}</span>
                </div>
                <div className="landing-consumer__trust-item">
                  <FontAwesomeIcon icon={faCheck} />
                  <span>{t("landing.consumers.hero.trust2")}</span>
                </div>
              </div>
            </div>

            <div className="landing-consumer__hero-visual">
              {/* Phone Mockup */}
              <div className="landing-consumer__phone-mockup">
                <div className="landing-floating-accent landing-consumer__floating-accent" />
                <div className="landing-consumer__phone-frame">
                  <img
                    src={customerAppImg}
                    alt="OFFOOD mobile app"
                    onError={handleImageError}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Stats integrated in hero */}
          <div className="landing-consumer__stats">
            <div className="landing-consumer__stat">
              <span className="landing-consumer__stat-value">
                {t("landing.consumers.stats.orderTime.value")}
              </span>
              <span className="landing-consumer__stat-label">
                {t("landing.consumers.stats.orderTime.label")}
              </span>
            </div>
            <div className="landing-consumer__stat">
              <span className="landing-consumer__stat-value">
                {t("landing.consumers.stats.wait.value")}
              </span>
              <span className="landing-consumer__stat-label">
                {t("landing.consumers.stats.wait.label")}
              </span>
            </div>
            <div className="landing-consumer__stat">
              <span className="landing-consumer__stat-value">
                {t("landing.consumers.stats.saved.value")}
              </span>
              <span className="landing-consumer__stat-label">
                {t("landing.consumers.stats.saved.label")}
              </span>
            </div>
            <div className="landing-consumer__stat">
              <span className="landing-consumer__stat-value">
                {t("landing.consumers.stats.rating.value")}
              </span>
              <span className="landing-consumer__stat-label">
                {t("landing.consumers.stats.rating.label")}
              </span>
            </div>
          </div>

          {/* Differentiator - Visual comparison points (inside hero) */}
          <div className="landing-consumer__differentiator-content">
            <div className="landing-consumer__differentiator-points">
              <div className="landing-consumer__differentiator-point">
                <FontAwesomeIcon
                  icon={faTimes}
                  className="landing-consumer__differentiator-icon landing-consumer__differentiator-icon--no"
                />
                <span>{t("landing.consumers.differentiator.notDelivery")}</span>
              </div>
              <div className="landing-consumer__differentiator-point">
                <FontAwesomeIcon
                  icon={faCheck}
                  className="landing-consumer__differentiator-icon landing-consumer__differentiator-icon--yes"
                />
                <span>
                  {t("landing.consumers.differentiator.realRestaurant")}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Pre-order Flow Explainer - With Images */}
        <section className="landing-section landing-consumer__preorder-flow">
          <div className="landing-consumer__section-container">
            <h2 className="landing-section-title landing-consumer__section-title">
              {t("landing.consumers.preorderFlow.title")}
            </h2>
            <p className="landing-section-subtitle landing-consumer__section-subtitle">
              {t("landing.consumers.preorderFlow.subtitle")}
            </p>

            <div className="landing-consumer__steps-grid">
              {steps.map((step, index) => (
                <div
                  key={index}
                  className={`landing-consumer__step-row ${
                    index % 2 === 1 ? "landing-consumer__step-row--reverse" : ""
                  }`}
                >
                  <div className="landing-step-card landing-consumer__step-card">
                    <div className="landing-step-number landing-consumer__step-number">
                      {index + 1}
                    </div>
                    <div className="landing-step-card__content landing-consumer__step-content">
                      <h3 className="landing-step-card__title landing-consumer__step-title">
                        {step.title}
                      </h3>
                      <p className="landing-step-card__description landing-consumer__step-description">
                        {step.description}
                      </p>
                    </div>
                  </div>
                  <div className="landing-step-image landing-consumer__step-image">
                    <img
                      src={step.image}
                      alt={step.title}
                      loading="lazy"
                      onError={handleImageError}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section - Visual Comparison */}
        <section
          className="landing-section landing-consumer__benefits"
          style={{ "--benefits-bg-image": `url(${benefitsBgImg})` }}
        >
          <div className="landing-consumer__section-container">
            <h2 className="landing-section-title landing-consumer__section-title">
              {t("landing.consumers.benefits.sectionTitle")}
            </h2>

            {/* Visual Comparison: Without vs With OFFOOD */}
            <div className="landing-consumer__comparison">
              {/* Without OFFOOD */}
              <div className="landing-comparison-card landing-comparison-card--without">
                <div className="landing-comparison-card__header">
                  <span className="landing-comparison-card__icon landing-comparison-card__icon--negative">
                    <FontAwesomeIcon icon={faTimes} />
                  </span>
                  <h3 className="landing-comparison-card__title">
                    {t("landing.consumers.benefits.comparison.without.title")}
                  </h3>
                </div>
                <ul className="landing-comparison-card__list">
                  <li>
                    <FontAwesomeIcon icon={faHourglass} />
                    <span>
                      {t("landing.consumers.benefits.comparison.without.wait1")}
                    </span>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faHourglass} />
                    <span>
                      {t("landing.consumers.benefits.comparison.without.wait2")}
                    </span>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faHourglass} />
                    <span>
                      {t("landing.consumers.benefits.comparison.without.wait3")}
                    </span>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faHourglass} />
                    <span>
                      {t("landing.consumers.benefits.comparison.without.wait4")}
                    </span>
                  </li>
                </ul>
                <div className="landing-comparison-card__footer">
                  <FontAwesomeIcon icon={faClock} />
                  <span>
                    {t("landing.consumers.benefits.comparison.without.total")}
                  </span>
                </div>
              </div>

              {/* With OFFOOD */}
              <div className="landing-comparison-card landing-comparison-card--with">
                <div className="landing-comparison-card__header">
                  <span className="landing-comparison-card__icon landing-comparison-card__icon--positive">
                    <FontAwesomeIcon icon={faCheck} />
                  </span>
                  <h3 className="landing-comparison-card__title">
                    {t("landing.consumers.benefits.comparison.with.title")}
                  </h3>
                </div>
                <ul className="landing-comparison-card__list">
                  <li>
                    <FontAwesomeIcon icon={faWalking} />
                    <span>
                      {t("landing.consumers.benefits.comparison.with.step1")}
                    </span>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faBagShopping} />
                    <span>
                      {t("landing.consumers.benefits.comparison.with.step2")}
                    </span>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faUtensils} />
                    <span>
                      {t("landing.consumers.benefits.comparison.with.step3")}
                    </span>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} />
                    <span>
                      {t("landing.consumers.benefits.comparison.with.step4")}
                    </span>
                  </li>
                </ul>
                <div className="landing-comparison-card__footer landing-comparison-card__footer--positive">
                  <FontAwesomeIcon icon={faClock} />
                  <span>
                    {t("landing.consumers.benefits.comparison.with.total")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section - shared .landing-faq* only (_landing-shared.scss) */}
        <section className="landing-section landing-faq landing-consumer__faq">
          <div className="landing-consumer__section-container">
            <h2 className="landing-section-title landing-consumer__section-title">
              {t("landing.consumers.faq.title")}
            </h2>

            <div className="landing-faq-list">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className={`landing-faq-item ${
                    openFaqIndex === index ? "landing-faq-item--open" : ""
                  }`}
                >
                  <button
                    type="button"
                    className="landing-faq-question"
                    onClick={() =>
                      setOpenFaqIndex(openFaqIndex === index ? null : index)
                    }
                    aria-expanded={openFaqIndex === index}
                  >
                    <span>{faq.question}</span>
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className="landing-faq-icon"
                    />
                  </button>
                  <div className="landing-faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section
          ref={finalCtaRef}
          className="landing-section landing-consumer__final-cta"
        >
          <div className="landing-consumer__final-cta-container">
            <h2 className="landing-consumer__cta-headline">
              {t("landing.consumers.finalCta.headline")}
            </h2>
            <p className="landing-consumer__cta-subheadline">
              {t("landing.consumers.finalCta.subheadline")}
            </p>

            {/* App Store Buttons */}
            <div className="landing-consumer__cta-buttons">
              <button
                className="landing-consumer__store-button"
                onClick={openPlayStore}
                data-analytics="cta-consumer-playstore"
                data-analytics-location="footer-cta"
              >
                <FontAwesomeIcon icon={faGooglePlayBrand} />
                <div className="landing-consumer__store-button-text">
                  <span>{t("home.google.download")}</span>
                  <span>{t("home.google.store")}</span>
                </div>
              </button>
              <button
                className="landing-consumer__store-button"
                onClick={openAppStore}
                data-analytics="cta-consumer-appstore"
                data-analytics-location="footer-cta"
              >
                <FontAwesomeIcon icon={faAppleBrand} />
                <div className="landing-consumer__store-button-text">
                  <span>{t("home.apple.download")}</span>
                  <span>{t("home.apple.store")}</span>
                </div>
              </button>
            </div>

            {/* Swiss trust signal */}
            <div className="landing-consumer__swiss-trust">
              <svg
                width="20"
                height="20"
                viewBox="0 0 32 32"
                aria-label="Switzerland"
                role="img"
              >
                <rect width="32" height="32" fill="#D52B1E" rx="2" />
                <path d="M13 6h6v7h7v6h-7v7h-6v-7H6v-6h7V6z" fill="#FFF" />
              </svg>
              <span>{t("landing.entry.trust.swissLabel")}</span>
            </div>
          </div>
        </section>

        {/* Banderole CTA - appears after hero, hides near final CTA; smart store redirect */}
        {showFloatingCta && (
          <div className="landing-banderole">
            <p className="landing-banderole__text">
              {t("landing.consumers.banderole.text")}
            </p>
            <div className="landing-banderole__cta">
              <Button
                variant="white"
                size="md"
                onClick={openStoreForDevice}
                data-analytics="cta-consumer-store"
                data-analytics-location="banderole"
              >
                {t("landing.consumers.banderole.cta")}
                <FontAwesomeIcon icon={faArrowDown} className="ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Landing Footer with all links */}
        <LandingFooter />
      </div>
    </>
  );
}

export default LandingConsumers;
